// Firestore database operations for RSVP data
import { 
  collection, 
  addDoc, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  updateDoc,
  doc,
  serverTimestamp 
} from 'firebase/firestore';
import { db } from './config';

export const rsvpService = {
  // Create a new RSVP
  async createRSVP(rsvpData) {
    try {
      console.log('Creating RSVP:', rsvpData);
      
      const rsvpCollection = collection(db, 'RSVPs');
      
      const rsvpDoc = {
        eventId: rsvpData.eventId,
        email: rsvpData.email.toLowerCase().trim(),
        name: rsvpData.name || '',
        phone: rsvpData.phone || '',
        status: rsvpData.status, // 'attending' | 'interested' | 'cannot_attend'
        eventDate: rsvpData.eventDate,
        eventLocation: rsvpData.eventLocation,
        reminderSent: false,
        createdAt: serverTimestamp(),
        timestamp: Date.now()
      };
      
      const docRef = await addDoc(rsvpCollection, rsvpDoc);
      console.log('✅ RSVP created with ID:', docRef.id);
      
      return { success: true, id: docRef.id, data: rsvpDoc };
    } catch (error) {
      console.error('❌ Error creating RSVP:', error);
      return { success: false, error: error.message };
    }
  },

  // Get RSVPs for a specific event
  async getRSVPsByEvent(eventId) {
    try {
      const rsvpCollection = collection(db, 'RSVPs');
      const q = query(
        rsvpCollection, 
        where('eventId', '==', eventId),
        orderBy('timestamp', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      const rsvps = [];
      
      querySnapshot.forEach((doc) => {
        rsvps.push({ id: doc.id, ...doc.data() });
      });
      
      return { success: true, data: rsvps };
    } catch (error) {
      console.error('❌ Error getting RSVPs:', error);
      return { success: false, error: error.message };
    }
  },

  // Check if email already RSVP'd for an event
  async checkExistingRSVP(eventId, email) {
    try {
      const rsvpCollection = collection(db, 'RSVPs');
      const q = query(
        rsvpCollection,
        where('eventId', '==', eventId),
        where('email', '==', email.toLowerCase().trim())
      );
      
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const existingRSVP = querySnapshot.docs[0];
        return { 
          exists: true, 
          data: { id: existingRSVP.id, ...existingRSVP.data() } 
        };
      }
      
      return { exists: false };
    } catch (error) {
      console.error('❌ Error checking existing RSVP:', error);
      return { exists: false, error: error.message };
    }
  },

  // Update RSVP status
  async updateRSVPStatus(rsvpId, newStatus) {
    try {
      const rsvpRef = doc(db, 'RSVPs', rsvpId);
      await updateDoc(rsvpRef, {
        status: newStatus,
        updatedAt: serverTimestamp()
      });
      
      console.log('✅ RSVP status updated');
      return { success: true };
    } catch (error) {
      console.error('❌ Error updating RSVP:', error);
      return { success: false, error: error.message };
    }
  },

  // Get RSVPs for reminders (events happening in specified days)
  async getRSVPsForReminders(daysAhead) {
    try {
      const targetDate = new Date();
      targetDate.setDate(targetDate.getDate() + daysAhead);
      targetDate.setHours(0, 0, 0, 0);
      
      const nextDay = new Date(targetDate);
      nextDay.setDate(nextDay.getDate() + 1);
      
      const rsvpCollection = collection(db, 'RSVPs');
      const q = query(
        rsvpCollection,
        where('reminderSent', '==', false),
        where('status', 'in', ['attending', 'interested'])
      );
      
      const querySnapshot = await getDocs(q);
      const rsvps = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        const eventDate = new Date(data.eventDate);
        eventDate.setHours(0, 0, 0, 0);
        
        // Check if event date matches target date
        if (eventDate.getTime() === targetDate.getTime()) {
          rsvps.push({ id: doc.id, ...data });
        }
      });
      
      return { success: true, data: rsvps };
    } catch (error) {
      console.error('❌ Error getting RSVPs for reminders:', error);
      return { success: false, error: error.message };
    }
  },

  // Mark reminder as sent
  async markReminderSent(rsvpId) {
    try {
      const rsvpRef = doc(db, 'RSVPs', rsvpId);
      await updateDoc(rsvpRef, {
        reminderSent: true,
        reminderSentAt: serverTimestamp()
      });
      
      return { success: true };
    } catch (error) {
      console.error('❌ Error marking reminder sent:', error);
      return { success: false, error: error.message };
    }
  }
};